import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { Company } from '@site-mate/dashpivot-shared-library';

import { FolderNavBarService } from 'app/layout/folder-navbar/folder-navbar.service';
import { RouteParamsService } from 'app/shared/service/route-params.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { UIComponentsTheme } from 'app/ui-components/ui-components-theme.enum';
import { IUserControllerMap, UserService } from 'app/user/user.service';

import { WorkspaceSwitcherModalCloseParams } from './workspace-switcher-modal-close-params.interface';

@Component({
  selector: 'cc-workspace-switcher',
  templateUrl: './workspace-switcher.component.html',
  styleUrls: ['./workspace-switcher.component.scss'],
})
export class WorkspaceSwitcherComponent implements OnDestroy, OnInit {
  companyList: Company[] = [];
  filtered = false;
  loading = true;
  unfilteredCompanyList: Company[] = [];
  userControllerMap: IUserControllerMap;
  isCompanyArchived = true;

  private closeParams: WorkspaceSwitcherModalCloseParams;
  readonly searchInputTheme = UIComponentsTheme.White;
  readonly searchDebounce = 250;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly folderNavbarService: FolderNavBarService,
    private readonly modal: NgbActiveModal,
    private readonly userService: UserService,
    private readonly routeParamsService: RouteParamsService,
    private readonly toastService: ToastrService,
  ) {}

  ngOnInit(): void {
    this.userControllerMap = this.userService.getUserControllerMap();
    if (this.userControllerMap.isAdmin) {
      this.resetCompanyList();
      this.loading = false;
    } else {
      this.folderNavbarService
        .getUserCompanies()
        .pipe(takeUntil(this.destroy$))
        .subscribe((companies) => {
          if (!companies.length) {
            this.loading = false;
            this.modal.close({ noCompaniesFound: true });
            this.toastService.clear();
          }
          this.unfilteredCompanyList = companies;
          this.resetCompanyList();
          this.loading = false;
        });
    }

    this.routeParamsService
      .get('company')
      .pipe(
        takeUntil(this.destroy$),
        filter((company) => !!company),
      )
      .subscribe((company) => {
        this.isCompanyArchived = company.archived;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.complete();
  }

  resetCompanyList() {
    this.companyList = this.userControllerMap.isAdmin ? [] : this.unfilteredCompanyList;
    this.filtered = false;
  }

  dismiss() {
    this.modal.dismiss();
  }

  createCompany() {
    this.closeParams = {
      addNewCompany: true,
      selectedCompanyId: null,
    };
    this.modal.close(this.closeParams);
  }

  selectCompany(companyId: string) {
    this.closeParams = {
      addNewCompany: false,
      selectedCompanyId: companyId,
    };
    this.modal.close(this.closeParams);
  }

  filterCompanies(searchTerm: string) {
    this.resetCompanyList();

    const searchTermTrimmed = searchTerm.trim();
    if (!searchTermTrimmed.length) {
      return;
    }

    if (this.userControllerMap.isAdmin) {
      this.superUserSearchCompany(searchTermTrimmed);
    } else {
      this.defaultUserSearchCompany(searchTermTrimmed);
    }
  }

  private superUserSearchCompany(searchTerm: string) {
    const minSearchCharacters = 1;

    if (searchTerm.length > minSearchCharacters) {
      this.loading = true;

      this.folderNavbarService
        .superUserCompanySearch(searchTerm)
        .pipe(takeUntil(this.destroy$))
        .subscribe((companies) => {
          this.companyList = companies;
          this.filtered = true;
          this.loading = false;
        });
    }
  }

  private defaultUserSearchCompany(searchTerm: string) {
    this.companyList = this.unfilteredCompanyList.filter((company) =>
      company.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    this.filtered = true;
  }
}
